import { Injectable } from '@angular/core';
import { Observable, of, Subject } from 'rxjs';
import { catchError, defaultIfEmpty, filter, map, mergeMap, switchMap, take, toArray } from 'rxjs/operators';

import { ApWindowService } from '@ap/cdk';
import { BerichtbeheerResource, BerichtInformatie, Status } from '@ap/interface-apic-berichtbeheer';
import { SessiebeheerResource, SessionStatus } from '@ap/interface-apic-sessiebeheer';
import { ContractDetails, MijnOverzichtResource, OpenstaandeOpdrachten } from '@ap/interface-apix-mijn-overzicht';
import { MijnAuthSessieManagerService } from '@ap/mijn-auth-sessie-manager';
import { MijnLaunchDarklyService } from '@ap/mijn-kanaalconfiguratie';
import { MijnNavigatieService } from '@ap/mijn-navigatie';
import { ApPersistenceService } from '@ap/persistence';

import { NEXTBESTACTION_SESSIONNAME, RESET_ROUTE_KEYS } from '../mijn-overzicht-template/mijn-overzicht-template.constant';
import { MijnFeedbackForm, mijnFeedbackFormDefault } from '../mijn-overzicht-template/mijn-overzicht-template.model';
import { MijnTemplateNavigation } from '../mijn-template.navigation';

import { Breadcrumb } from './breadcrumb/breadcrumb.model';

@Injectable({ providedIn: 'root' })
export class PegaOverzichtTemplateService {
    private readonly nrOfUnreadMessages = new Subject<number>();
    private readonly currentSession = '0';

    public constructor(
        private readonly apWindowService: ApWindowService,
        private readonly berichtbeheerResource: BerichtbeheerResource,
        private readonly mijnNavigatieService: MijnNavigatieService,
        private readonly mijnTemplateNavigation: MijnTemplateNavigation,
        private readonly launchDarklyService: MijnLaunchDarklyService,
        private readonly mijnOverzichtResource: MijnOverzichtResource,
        private readonly persistenceService: ApPersistenceService,
        private readonly sessiebeheerResource: SessiebeheerResource,
        private readonly mijnKlantLoginService: MijnAuthSessieManagerService
    ) {}

    private static hasAllowedPriority(berichtInformatie: BerichtInformatie): boolean {
        return berichtInformatie.prioriteit < 21 || berichtInformatie.prioriteit > 29;
    }

    private static isResetUrl(url: string): boolean {
        const routeKeyForUrl = MijnNavigatieService.getRouteKeyForUrl(url);
        return RESET_ROUTE_KEYS.includes(routeKeyForUrl);
    }

    public getNrOfUnreadMessages(): Subject<number> {
        return this.nrOfUnreadMessages;
    }

    public loadNrOfUnreadMessages(): void {
        this.berichtbeheerResource
            .getBerichtenInformatie(Status.ONGELEZEN)
            .pipe(
                mergeMap((pagina) => pagina.berichten_informatie),
                filter(PegaOverzichtTemplateService.hasAllowedPriority),
                toArray(),
                map((berichten) => berichten.length),
                catchError(() => of(0))
            )
            .subscribe((result: number) => {
                this.nrOfUnreadMessages.next(result);
            });
    }

    public getContractDetails(): Observable<ContractDetails> {
        return this.mijnOverzichtResource.getContractDetails().pipe(catchError(() => of(null)));
    }

    public getNrOfSendableOrders(): Observable<number> {
        return this.mijnOverzichtResource.getOpenstaandeOpdrachten().pipe(
            map((result: OpenstaandeOpdrachten) => +result.aantal_te_verzenden_opdrachten),
            catchError(() => of(0))
        );
    }

    public terminateUserSession(): Observable<void> {
        return this.mijnKlantLoginService.isAuthenticated$.pipe(
            take(1),
            switchMap((authenticated) => {
                if (authenticated) {
                    return this.mijnKlantLoginService.logout();
                }
                return this.sessiebeheerResource.patchSession(this.currentSession, { status: SessionStatus.TERMINATED });
            }),
            catchError(() => {
                console.error('error logging out');
                return of();
            })
        );
    }

    public storeUrl(url?: string, title?: string): void {
        const storageUrl = url?.length > 0 ? url : (this.apWindowService.location_href() as string);
        const isResetUrl = PegaOverzichtTemplateService.isResetUrl(storageUrl);
        this.mijnNavigatieService.storeUrl(storageUrl, isResetUrl, title);
    }

    public getDynamicBreadcrumb(): Breadcrumb {
        const isMijnUrl = this.mijnTemplateNavigation.isMijnUrl();
        const history = this.mijnNavigatieService.getHistory();
        if (isMijnUrl && history?.enabled && history.routes.length > 1) {
            const previousRoute = history.routes[history.routes.length - 2];
            return {
                label: previousRoute.breadcrumb,
                path: previousRoute.url,
                dynamic: true
            };
        }
        return null;
    }

    public clearDynamicBreadcrumbs(): void {
        this.mijnNavigatieService.clearHistory();
    }

    public clearNBASession(): void {
        this.persistenceService.clearSession(NEXTBESTACTION_SESSIONNAME);
    }

    public getMijnFeedbackSettings(key: string): Observable<MijnFeedbackForm | null> {
        return this.launchDarklyService.getFlagValue(key, mijnFeedbackFormDefault).pipe(
            take(1),
            map(
                (json) =>
                    ({
                        moodId: json.moodId,
                        commentsId: json.commentsId,
                        channelId: json.channelId
                    }) as MijnFeedbackForm
            ),
            filter((form) => form !== null && form.commentsId !== undefined && form.moodId !== undefined && form.channelId !== undefined),
            defaultIfEmpty(null)
        );
    }
}
